import { LogoAdelante } from '@/components/layout/LogoAdelante';
import { Button } from '@/components/ui/button';
import { cliente, guardarSesionFiori } from '@/lib/cliente';
import { cn } from '@/lib/utils';
import type { OperadorFioriPublico } from '@adelante/shared';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, Delete, Truck } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

/**
 * `/fiori-login` — Autenticación por PIN para los choferes/operadores de
 * las autocargables Fiori (rol OperadorFiori). A diferencia de
 * `/lab-login` (un solo PIN compartido), acá cada operador tiene su
 * propio PIN, así que el login es en 2 pasos:
 *
 *   1) Elegir su nombre de una lista (viene de un endpoint público, sin
 *      auth — solo id + nombre, nunca el PIN).
 *   2) Teclado numérico de 4 dígitos, igual que `/lab-login`.
 *
 * En éxito: guarda el JWT en localStorage y redirige a `/coladas`
 * (el backend ya acota ese listado a lo que este operador registró).
 *
 * No usa MSAL — accesible sin login Entra ID.
 */

const TECLAS_NUMERICAS = [
  { id: 't1', valor: '1' },
  { id: 't2', valor: '2' },
  { id: 't3', valor: '3' },
  { id: 't4', valor: '4' },
  { id: 't5', valor: '5' },
  { id: 't6', valor: '6' },
  { id: 't7', valor: '7' },
  { id: 't8', valor: '8' },
  { id: 't9', valor: '9' },
  { id: 'tempty', valor: '' },
  { id: 't0', valor: '0' },
  { id: 'tborrar', valor: 'borrar' },
] as const;

export function FioriLoginPantalla() {
  const navegar = useNavigate();
  const operadoresQ = useQuery({
    queryKey: ['auth', 'fiori', 'operadores-publicos'],
    queryFn: () => cliente.authFiori.operadoresPublicos(),
  });
  const operadores = operadoresQ.data?.data?.operadores ?? [];

  const [operador, setOperador] = useState<OperadorFioriPublico | null>(null);

  if (!operador) {
    return (
      <PantallaBase subtitulo="Registro de coladas Fiori">
        <h1 className="mb-1 text-center text-xl font-semibold text-zinc-900">¿Quién sos?</h1>
        <p className="mb-6 text-center text-sm text-zinc-500">Elegí tu nombre para continuar</p>

        {operadoresQ.isLoading && <p className="text-center text-sm text-zinc-500">Cargando…</p>}
        {operadoresQ.error && (
          <p className="rounded-md border border-red-200 bg-red-50 p-3 text-center text-sm text-red-800">
            No se pudo cargar la lista de operadores: {operadoresQ.error.message}
          </p>
        )}
        {!operadoresQ.isLoading && !operadoresQ.error && operadores.length === 0 && (
          <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-center text-sm text-amber-800">
            Todavía no hay operadores dados de alta. Pedile a un administrador que te cree un acceso
            desde <code className="rounded bg-white px-1">/admin/fiori-operadores</code>.
          </p>
        )}

        <div className="space-y-2">
          {operadores.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => setOperador(o)}
              className="flex h-14 w-full items-center rounded-xl bg-zinc-100 px-4 text-left text-lg font-medium text-zinc-900 transition active:scale-[0.98] active:bg-zinc-200"
            >
              {o.nombre}
            </button>
          ))}
        </div>

        <div className="mt-6 border-t pt-4 text-center">
          <Button
            variant="link"
            size="sm"
            onClick={() => navegar('/')}
            className="text-xs text-zinc-500"
          >
            ¿Sos administrador u operador? Entrá con tu cuenta de correo
          </Button>
        </div>
      </PantallaBase>
    );
  }

  return <PantallaPin operador={operador} onVolver={() => setOperador(null)} />;
}

function PantallaPin({
  operador,
  onVolver,
}: {
  operador: OperadorFioriPublico;
  onVolver: () => void;
}) {
  const navegar = useNavigate();
  const [pin, setPin] = useState('');
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shake, setShake] = useState(false);

  const enviar = useCallback(
    async (pinCompleto: string) => {
      setCargando(true);
      setError(null);
      try {
        const r = await cliente.authFiori.login({ id_operador: operador.id, pin: pinCompleto });
        if (r.data) {
          guardarSesionFiori(r.data.token, r.data.nombre, r.data.expira_en);
          toast.success(`Bienvenido ${r.data.nombre}`);
          navegar('/coladas/registrar-fiori');
        } else {
          const msg = r.error?.mensaje ?? 'PIN incorrecto';
          setError(msg);
          setPin('');
          setShake(true);
          setTimeout(() => setShake(false), 600);
        }
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Error desconocido';
        setError(msg);
        setPin('');
        setShake(true);
        setTimeout(() => setShake(false), 600);
      } finally {
        setCargando(false);
      }
    },
    [navegar, operador.id],
  );

  useEffect(() => {
    if (pin.length === 4 && !cargando) {
      void enviar(pin);
    }
  }, [pin, cargando, enviar]);

  const teclear = (digito: string) => {
    if (cargando) return;
    setError(null);
    setPin((prev) => (prev.length < 4 ? prev + digito : prev));
  };

  const borrar = () => {
    if (cargando) return;
    setError(null);
    setPin((prev) => prev.slice(0, -1));
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (cargando) return;
      if (/^[0-9]$/.test(e.key)) {
        setError(null);
        setPin((prev) => (prev.length < 4 ? prev + e.key : prev));
      } else if (e.key === 'Backspace') {
        setError(null);
        setPin((prev) => prev.slice(0, -1));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cargando]);

  return (
    <PantallaBase subtitulo="Registro de coladas Fiori">
      <button
        type="button"
        onClick={onVolver}
        disabled={cargando}
        className="mb-4 flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-700 disabled:opacity-40"
      >
        <ChevronLeft className="h-4 w-4" /> No soy {operador.nombre}
      </button>

      <h1 className="mb-1 text-center text-xl font-semibold text-zinc-900">
        Hola, {operador.nombre}
      </h1>
      <p className="mb-6 text-center text-sm text-zinc-500">Ingresá tu PIN (4 dígitos)</p>

      <div
        className={cn(
          'mb-6 flex justify-center gap-4 transition-transform',
          shake && 'animate-shake',
        )}
      >
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className={cn(
              'h-4 w-4 rounded-full border-2 transition-all',
              pin.length > i
                ? 'border-emerald-600 bg-emerald-600 scale-110'
                : 'border-zinc-300 bg-transparent',
              error && 'border-red-500 bg-red-500/20',
            )}
          />
        ))}
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-center text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="grid grid-cols-3 gap-3">
        {TECLAS_NUMERICAS.map((t) => {
          if (t.valor === '') return <div key={t.id} />;
          if (t.valor === 'borrar') {
            return (
              <button
                key={t.id}
                type="button"
                onClick={borrar}
                disabled={cargando || pin.length === 0}
                className="flex h-16 items-center justify-center rounded-xl bg-zinc-100 text-zinc-700 transition active:scale-95 active:bg-zinc-200 disabled:opacity-40 disabled:active:scale-100"
                aria-label="Borrar"
              >
                <Delete className="h-6 w-6" />
              </button>
            );
          }
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => teclear(t.valor)}
              disabled={cargando}
              className="h-16 rounded-xl bg-zinc-100 text-2xl font-semibold text-zinc-900 transition active:scale-95 active:bg-zinc-200 disabled:opacity-40 disabled:active:scale-100"
              aria-label={`Dígito ${t.valor}`}
            >
              {t.valor}
            </button>
          );
        })}
      </div>

      {cargando && <div className="mt-4 text-center text-xs text-zinc-500">Verificando…</div>}
    </PantallaBase>
  );
}

function PantallaBase({
  children,
  subtitulo,
}: {
  children: React.ReactNode;
  subtitulo: string;
}) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#0A0A0A] px-4 py-8">
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-6 flex flex-col items-center gap-3">
          <LogoAdelante altura={28} />
          <span className="text-sm font-bold tracking-widest text-zinc-900">ADELANTE.</span>
          <span className="flex items-center gap-1 text-xs uppercase tracking-widest text-zinc-500">
            <Truck className="h-3.5 w-3.5" /> {subtitulo}
          </span>
        </div>
        {children}
      </div>
      <p className="mt-6 text-xs text-zinc-500">AD Concreto · Adelante Desarrollos</p>
      <style>{`
        @keyframes shake-x {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-8px); }
          40% { transform: translateX(8px); }
          60% { transform: translateX(-6px); }
          80% { transform: translateX(6px); }
        }
        .animate-shake { animation: shake-x 0.5s ease-in-out; }
      `}</style>
    </div>
  );
}
